import React, { useEffect } from 'react';
import { setUser } from '../redux/user/actions';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Card, CardHeader, CardMedia, CardContent, CardActions, Button, Avatar, IconButton } from '@mui/material';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HelpIcon from '@mui/icons-material/Help';
import { client, filterUser, TOGGLE_FAVORITE } from '../graphqlClient/graphql';
import { serverConfig } from '../api/apiConfig';

const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: theme.spacing(3),
        padding: theme.spacing(2),
    },
    card: {
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        },
    },
    cardContent: {
        flexGrow: 1,
        userSelect: 'none',
        msUserSelect: 'none',
        MozUserSelect: 'none',
        WebkitUserSelect: 'none',
    },
}));

const FavoritesPage: React.FC<PropsFromRedux> = ({ usuario, setUser }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [favorites, setFavorites] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);

    const getFavoritesAsync = async () => {
        if (!usuario?.favorites?.length) {
            setFavorites([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // We can use filterUser with IDs if the backend supports it, 
            // but the backend userFilter currently doesn't easily support a list of IDs 
            // except via the profile filter hack. 
            // However, we can just fetch all users and filter client-side for now, 
            // or better, if the backend supports it.
            // Looking at users.resolver.ts, it doesn't seem to have a specific "favorites" query.
            // So I'll fetch them individually or use a filter.

            const result = await filterUser({ role: 'scout' });
            if (Array.isArray(result)) {
                const favoritedUsers = result.filter((u: any) => usuario?.favorites?.includes(u._id));
                setFavorites(favoritedUsers);
            }
        } catch (error) {
            console.error('Error fetching favorites:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        getFavoritesAsync();
    }, [usuario]);

    const handleToggleFavorite = async (targetUserId: string) => {
        try {
            const result: any = await client.mutate({
                mutation: TOGGLE_FAVORITE,
                variables: { targetUserId },
            });
            if (result.data.toggleFavorite) {
                const newFavorites = result.data.toggleFavorite.favorites;
                // Since this page shows only favorites, we update the list
                setFavorites(prev => prev.filter(u => u._id !== targetUserId));
                // Sync with Redux
                const newUser = { ...usuario, favorites: newFavorites };
                setUser(newUser);
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    return (
        <Container className={classes.root}>
            <Typography variant="h4" gutterBottom align="center">
                My Favorites
            </Typography>

            {loading ? (
                <Typography align="center">Loading...</Typography>
            ) : favorites.length === 0 ? (
                <Typography align="center">No favorites yet.</Typography>
            ) : (
                <div className={classes.listContainer}>
                    {favorites.map((user: any, index: any) => (
                        <Card className={classes.card} key={index}>
                            <CardHeader
                                avatar={
                                    <Avatar sx={{ bgcolor: '#0df228' }} aria-label="recipe">
                                        {user.name.substring(0, 2)}
                                    </Avatar>
                                }
                                title={user.name}
                                subheader={user.grade ? 'score: ' + user.grade : ' '}
                                action={
                                    <IconButton
                                        aria-label="remove from favorites"
                                        onClick={() => handleToggleFavorite(user._id)}
                                        color="secondary"
                                    >
                                        <FavoriteIcon />
                                    </IconButton>
                                }
                            />
                            <CardMedia
                                style={{ backgroundColor: 'white' }}
                                component="img"
                                height="194"
                                image={user.image ? serverConfig.dev.url + 'images/' + user.image : "https://robohash.org/" + user.name}
                                alt="User image"
                            />
                            <CardContent className={classes.cardContent}>
                                <Typography variant="body2" color="textSecondary">
                                    {user.category?.name || 'No category'} - {user.country || 'No location'}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    onClick={() => navigate('/scoutme', { state: { userId: user._id } })}
                                >
                                    Professional info
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </div>
            )}
        </Container>
    );
};

const mapStateToProps = (state: any) => ({
    usuario: state.user.user,
});

const mapDispatchToProps = {
    setUser
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FavoritesPage);
